import { useCallback, useState } from "react";

const MAX_RECENT = 8;

const keyFor = (userId: string) => `lytra:archive-recent-searches:${userId}`;

function read(key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed)
      ? parsed.filter((q): q is string => typeof q === "string").slice(0, MAX_RECENT)
      : [];
  } catch {
    return [];
  }
}

function write(key: string, queries: string[]) {
  try {
    if (queries.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(queries));
  } catch {
    // Storage can be blocked (private window); the list then lives for this open only.
  }
}

/**
 * The user's last archive searches, newest first. Kept in this browser only
 * (per user, so a shared machine does not leak one person's queries to the
 * next): a convenience, not data anyone else needs to see.
 */
export function useRecentSearches(userId: string) {
  const key = keyFor(userId);
  const [recent, setRecent] = useState<string[]>(() => read(key));

  const remember = useCallback(
    (query: string) => {
      const q = query.trim();
      if (q.length < 2) return;
      // Re-read so two open tabs do not overwrite each other's entries.
      const next = [q, ...read(key).filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(
        0,
        MAX_RECENT,
      );
      write(key, next);
      setRecent(next);
    },
    [key],
  );

  const remove = useCallback(
    (query: string) => {
      const next = read(key).filter((r) => r !== query);
      write(key, next);
      setRecent(next);
    },
    [key],
  );

  const clear = useCallback(() => {
    write(key, []);
    setRecent([]);
  }, [key]);

  return { recent, remember, remove, clear };
}
